import { FC, useMemo, useState } from "react"
import { Box, Chip, MenuItem, TextField } from "@mui/material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { AlertColor } from "@mui/material"
import { DataGrid } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { formatDateAndTime } from "@/core/utils"
import { GuestBot } from "../../interfaces"
import { useLogsListQuery, useBotLogsListQuery } from "../../store"

interface IProps {
  botId?: string
  /** Журнальный фильтр источника (`campaign:<id>`); URL остаётся во владении страницы. */
  source?: string
  /** Сброс фильтра (крестик на чипе); не передаётся глобальной страницей логов. */
  onClearSource?: () => void
}

const STATUS_LABEL: Record<GuestBot.LogStatus, string> = {
  enqueued: "В очереди",
  published: "Отправлено",
  expired: "Просрочено",
  dropped: "Отброшено",
  failed: "Ошибка",
}

const STATUS_COLOR: Record<GuestBot.LogStatus, AlertColor | "default"> = {
  enqueued: "default",
  published: "success",
  expired: "warning",
  dropped: "warning",
  failed: "error",
}

const STATUS_FILTER_OPTIONS: GuestBot.LogStatus[] = [
  "enqueued",
  "published",
  "expired",
  "dropped",
  "failed",
]

export const LogsTable: FC<IProps> = ({ botId, source, onClearSource }) => {
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const [status, setStatus] = useState<GuestBot.LogStatus | "">("")

  const queryParams = {
    limit: pageSize,
    offset: page * pageSize,
    ...(status ? { status } : {}),
    ...(source ? { source } : {}),
  }

  const botQuery = useBotLogsListQuery(
    { pathParams: { id: botId as string }, queryParams },
    { skip: !botId },
  )
  const allQuery = useLogsListQuery({ queryParams }, { skip: !!botId })

  const { data, isFetching } = botId ? botQuery : allQuery
  const rows = data?.items ?? []
  const rowCount = data?.total ?? 0

  const columns: GridColDef<GuestBot.LogRow>[] = useMemo(
    () => [
      {
        field: "triggeredAt",
        headerName: "Время",
        flex: 2,
        valueGetter: (params) => formatDateAndTime(params.row.triggeredAt),
      },
      {
        field: "status",
        headerName: "Статус",
        flex: 1,
        renderCell: (params) => (
          <Chip
            size="small"
            label={STATUS_LABEL[params.row.status]}
            color={STATUS_COLOR[params.row.status]}
          />
        ),
      },
      { field: "botId", headerName: "Bot ID", flex: 1 },
      {
        field: "triggerText",
        headerName: "Триггер",
        flex: 2,
        valueGetter: (params) => params.row.triggerText ?? "(fallback)",
      },
      { field: "senderName", headerName: "Отправитель", flex: 2 },
      { field: "chatTitle", headerName: "Чат", flex: 2 },
      { field: "replyType", headerName: "Тип ответа", flex: 1 },
      { field: "error", headerName: "Ошибка", flex: 2 },
    ],
    [],
  )

  return (
    <>
      <Box mb={1} display="flex" justifyContent="flex-end">
        {source && onClearSource && (
          <Chip
            size="small"
            label={`Источник: ${source}`}
            onDelete={onClearSource}
            sx={{ mr: 1 }}
          />
        )}
        <TextField
          select
          size="small"
          label="Статус"
          value={status}
          onChange={(e) => setStatus(e.target.value as GuestBot.LogStatus | "")}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Все</MenuItem>
          {STATUS_FILTER_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box height="70vh">
        <DataGrid<GuestBot.LogRow>
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={isFetching}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
        />
      </Box>
    </>
  )
}
